import React, { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useSelector, useDispatch } from 'react-redux';

import { Paginate, List, Empty } from 'app/seedly-component-library';
import { getProductsNextPage } from 'app/dispatcher/productDispatcher';
import ProductItemWithDetail from 'app/components/product/ProductItemWithDetail';
import SignUpBonusCard from 'app/components/review/SignUpBonusCard';
import { getCurrentCategory } from 'app/selectors/productSelector';

import useIsMobile from 'app/utils/hooks/useIsMobile';
import mixpanel, { trackedEvents } from 'utils/mixpanel';

import type { RootState } from 'app/reducer';
import { PRODUCT_FILTER_TYPE } from 'app/constants/product';

interface OwnProps {
  isSignUpBonus: boolean;
  hidePagination?: boolean;
  hideSignUpBonusEntryPoint: boolean;
  customFooter?: React.ReactNode;
  renderMarketingBanner?: () => React.ReactNode;
  onPageChange: () => void;
}

const ProductDetailList = (props: OwnProps) => {
  const {
    onPageChange,
    hidePagination,
    renderMarketingBanner,
    customFooter,
    hideSignUpBonusEntryPoint,
    isSignUpBonus,
  } = props;

  const productMeta = useSelector((state: RootState) => state.product.meta);
  let products = useSelector((state: RootState) => state.product.products);

  const isFetching = useSelector(
    (state: RootState) => state.product.api.isFetching,
  );
  const productFilter = useSelector(
    (state: RootState) => state.product.productFilterParams.filter,
  );

  const dispatch = useDispatch();
  const router = useRouter();
  const isMobile = useIsMobile();
  const currentCategory = useSelector(getCurrentCategory);

  const { currentPage, totalPage } = productMeta.pagination;

  const renderPaginate = totalPage && totalPage > 1 && !hidePagination;

  const handleLoadMoreProducts = data => {
    if (onPageChange) onPageChange();
    const page = data.selected + 1;
    const { categorySlug, sortType, term } = productMeta;
    const productParams = {
      categorySlug,
      sortType,
      page,
      term,
      productFilter,
      isSignUpBonus,
    };
    return dispatch(getProductsNextPage(productParams));
  };

  if (
    isSignUpBonus &&
    (currentCategory?.slug === 'car-insurance' ||
      currentCategory?.slug === 'credit-cards') &&
    productMeta.sortType === PRODUCT_FILTER_TYPE.MOST_POPULAR
  ) {
    products = products.sort((a, b) => {
      const aRanking = a.details.info?.find(i => i.name === 'ranking')?.value;
      const bRanking = b.details.info?.find(i => i.name === 'ranking')?.value;

      if (aRanking === null) {
        return 1;
      }

      return Number(aRanking) - Number(bRanking);
    });
  }

  // Experimental
  let productsWithBanner;
  if (renderMarketingBanner) {
    const position = 3;
    productsWithBanner = [
      ...products.slice(0, position),
      renderMarketingBanner(),
      ...products.slice(position),
    ].filter(Boolean);
  } else {
    productsWithBanner = [...products];
  }

  const productsDataSource = productsWithBanner.map(product => {
    if (React.isValidElement(product)) {
      return product;
    }

    return <ProductItemWithDetail key={product.id} product={product} />;
  });

  const dataSource = useMemo(() => {
    const dup = [...productsDataSource];

    if (isMobile && !hideSignUpBonusEntryPoint) {
      dup.splice(
        3,
        0,
        <SignUpBonusCard
          TitleComponent={
            <>
              Get amazing deals when you sign up for new financial products on
              Seedly 🎁
            </>
          }
          ctaText="See All Offers"
          onCTAClick={() => {
            mixpanel.track(trackedEvents.SignUpBonus, {
              param: 'see_all_offers',
            });
            router.push('/reviews/sign-up-bonus/');
          }}
        />,
      );
      return dup;
    }

    return dup;
  }, [hideSignUpBonusEntryPoint, isMobile, productsDataSource, router]);

  return (
    <List
      dataSource={dataSource}
      isLoading={isFetching}
      width="100%"
      listSpacing="xs"
      empty={<Empty title="No products found!" />}
      footer={
        <div className="flex flex-col w-full my-6 items-center">
          {customFooter}
          {renderPaginate && (
            <Paginate
              pageRangeDisplayed={2}
              pageCount={totalPage}
              forcePage={currentPage - 1}
              onPageChange={handleLoadMoreProducts}
            />
          )}
        </div>
      }
    />
  );
};

export default ProductDetailList;
